import {
  HighlightIcon,
  type PanelProps,
  SidePanel,
} from '@dev-spendesk/grapes';
import classNames from 'classnames';
import React from 'react';

import { useTranslation } from '../../hooks/useTranslation';

type DroppablePanelProps = PanelProps & {
  onUploadFiles(files: File[]): void | Promise<void>;
  canUploadFiles?: boolean;
  allowMultipleFiles?: boolean;
};

export const DroppablePanel = ({
  onUploadFiles,
  canUploadFiles = true,
  allowMultipleFiles = false,
  className,
  children,
  ...rest
}: DroppablePanelProps) => {
  const [isDragActive, setDragActive] = React.useState(false);

  const currentDragTarget = React.useRef<EventTarget | null>(null);

  const handleDragLeave = React.useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      const { target } = event;
      // DragEnter is triggered before DragLeave, so currentDragTarget is the latest known target
      // If we leave this target, it means we are leaving the droppable area
      if (currentDragTarget.current === target) {
        setDragActive(false);
      }
    },
    [],
  );

  const handleDragEnter = React.useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      const { target } = event;
      currentDragTarget.current = target;
      setDragActive(true);
    },
    [],
  );

  const handleDragOver = React.useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      const { dataTransfer } = event;
      if (dataTransfer) {
        try {
          dataTransfer.dropEffect = 'copy';
        } catch {
          // Do nothing, some browsers can have readonly dropEffect
        }
      }
    },
    [],
  );

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const { files } = event.dataTransfer;
    const filesArray = Array.from(files).filter((_, index) =>
      allowMultipleFiles ? true : index < 1,
    );
    onUploadFiles(filesArray);
    setDragActive(false);
  };

  return (
    <SidePanel
      {...rest}
      className={classNames(
        className,
        isDragActive ? 'bg-background-secondary-brand-default [&_div]:h-full' : '',
      )}
      {...(canUploadFiles
        ? {
            onDrop: handleDrop,
            onDragLeave: handleDragLeave,
            onDragEnter: handleDragEnter,
            onDragOver: handleDragOver,
            'aria-dropeffect': 'copy',
          }
        : {})}
      {...(isDragActive
        ? {
            header: undefined,
            footer: undefined,
            title: undefined,
            footerSummary: undefined,
            onClose: undefined,
          }
        : {})}
    >
      {isDragActive ? <ActiveDragContent /> : children}
    </SidePanel>
  );
};

/**
 * Helpers
 */

const ActiveDragContent = () => {
  const { t } = useTranslation('global');
  return (
    <div className="flex flex-col items-center justify-center gap-32 p-24 text-content-secondary-bg-primary">
      <HighlightIcon
        variant="purple"
        name="mouse-square-stack"
        size={56}
        className="!h-56"
      />
      <span>{t('misc.dragAndDropFiles')}</span>
    </div>
  );
};
